import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { formatDistanceStrict, formatDistanceToNow } from "date-fns";
import {
  DataTable,
  localTablePage,
  Mono,
  Segmented,
  StatusBadge,
  TableIdentity,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import type { ReloadAlert } from "@repo/shared";

import { KpiCell, KpiStrip } from "@/components/app-analysis/kpi";
import { DensityToggle } from "@/components/density-toggle";
import { supportCode } from "@/components/error-screen";
import { reloadAlertsQueryOptions } from "@/features/reloads/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { useLocale } from "@/hooks/use-locale";
import { formatSeconds } from "@/lib/duration-format";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";
import { appLabel } from "./labels";
import { ViewRunsButton } from "./AppRunsDrawer";
import { QlikAppLink } from "./QlikAppLink";

const PAGE_SIZE = 50;

type AlertFilters = { kind?: ReloadAlert["kind"] };

// The rules' words render in the strong ink; the rest of the note stays faint.
const RULE_TERM = { b: <b className="font-medium text-dim" /> };

export function AlertsView({
  connectionId,
  baseUrl,
  version,
  onViewRuns,
}: {
  connectionId: string;
  baseUrl: string;
  version: string;
  onViewRuns: (appId: string) => void;
}) {
  const { t } = useTranslation("reloads");
  const { t: tc } = useTranslation("common");
  const { language, dateFnsLocale } = useLocale();
  const { formatDateTime } = useFormatDate();
  const { density, setDensity } = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  const query = useQuery(reloadAlertsQueryOptions(connectionId, version));
  const alerts = query.data ?? [];

  const [state, setState] = useState<DataTableState<AlertFilters>>({
    page: 1,
    pageSize: PAGE_SIZE,
    query: "",
    filters: {},
    selection: [],
  });

  const count = (value: number) => (query.data ? value.toLocaleString(language) : "—");
  const ofKind = (kind: ReloadAlert["kind"]) =>
    alerts.filter((alert) => alert.kind === kind).length;

  const whatHappened = (alert: ReloadAlert) => {
    switch (alert.kind) {
      case "failing":
        return (
          <>
            {t("alerts.what.failing", {
              count: alert.streak,
              formatted: alert.streak.toLocaleString(language),
            })}
            {alert.errorCode && (
              <>
                {" · "}
                <Mono>{alert.errorCode}</Mono>
              </>
            )}
          </>
        );
      case "slower":
        return t("alerts.what.slower", {
          count: alert.recentSamples,
          recent: formatSeconds(alert.recentMedianSeconds),
          base: formatSeconds(alert.baselineMedianSeconds),
          ratio: (alert.recentMedianSeconds / alert.baselineMedianSeconds).toLocaleString(
            language,
            { maximumFractionDigits: 1 },
          ),
        });
      case "stopped":
        return t("alerts.what.stopped", {
          ago: formatDistanceToNow(new Date(alert.lastReloadAt), { locale: dateFnsLocale }),
          interval: formatDistanceStrict(0, alert.typicalIntervalSeconds * 1000, {
            locale: dateFnsLocale,
          }),
        });
    }
  };

  const since = (alert: ReloadAlert) => {
    if (alert.kind === "failing") return formatDateTime(alert.since);
    if (alert.kind === "stopped") return formatDateTime(alert.lastReloadAt);
    return "—";
  };

  const columns: DataTableColumn<ReloadAlert>[] = [
    {
      id: "kind",
      header: t("alerts.columns.alert"),
      width: "13rem",
      cell: (alert) => (
        <StatusBadge tone={alert.kind === "failing" ? "danger" : "warning"}>
          {t(`alerts.kind.${alert.kind}`)}
        </StatusBadge>
      ),
    },
    {
      id: "app",
      header: t("alerts.columns.app"),
      width: "16rem",
      variant: "identity",
      rowHeader: true,
      cell: (alert) => <TableIdentity name={<QlikAppLink app={alert} baseUrl={baseUrl} />} />,
    },
    {
      id: "what",
      header: t("alerts.columns.what"),
      wrap: true,
      cell: whatHappened,
    },
    {
      id: "since",
      header: t("alerts.columns.since"),
      width: "11rem",
      align: "end",
      variant: "numeric",
      cell: since,
    },
  ];

  const page = localTablePage(alerts, state, {
    columns,
    search: appLabel,
    filter: (alert, { kind }) => !kind || alert.kind === kind,
  });

  const countLabel = (label: string, total: number) => (
    <>
      {label} <Mono>{total.toLocaleString(language)}</Mono>
    </>
  );

  const supportInfo = supportCode(query.error, tc("error.supportCode"));

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-faint">{t("alerts.measured")}</p>

      <KpiStrip>
        <KpiCell
          label={t("alerts.kpi.failing")}
          value={count(ofKind("failing"))}
          subMeta={t("alerts.kpi.failingHint")}
        />
        <KpiCell
          label={t("alerts.kpi.slower")}
          value={count(ofKind("slower"))}
          subMeta={t("alerts.kpi.slowerHint")}
        />
        <KpiCell
          label={t("alerts.kpi.stopped")}
          value={count(ofKind("stopped"))}
          subMeta={t("alerts.kpi.stoppedHint")}
        />
      </KpiStrip>

      <div className="flex flex-col gap-3">
        <DataTable
          label={t("tabs.alerts")}
          density={density}
          columns={columns}
          rows={page.rows}
          getRowId={(alert) => `${alert.kind}:${alert.appId}`}
          state={state}
          onStateChange={setState}
          loading={query.isPending}
          refreshing={listRefreshing(query)}
          error={
            query.isError
              ? {
                  title: t("error.load"),
                  description: supportInfo && (
                    <>
                      {supportInfo.label}: <Mono>{supportInfo.value}</Mono>
                    </>
                  ),
                }
              : null
          }
          onRetry={() => void query.refetch()}
          search={{
            ...tableSearch(t("alerts.searchPlaceholder")),
            inputRef: searchRef,
            shortcutHint: "/",
          }}
          toolbar={
            <>
              <Segmented<"all" | ReloadAlert["kind"]>
                size="compact"
                label={t("alerts.filterLabel")}
                value={state.filters.kind ?? "all"}
                onValueChange={(value) =>
                  setState((prev) =>
                    updateTableState(prev, {
                      filters: value === "all" ? {} : { kind: value },
                    }),
                  )
                }
                options={[
                  { value: "all", label: countLabel(t("alerts.filterAll"), alerts.length) },
                  {
                    value: "failing",
                    label: countLabel(t("alerts.kind.failing"), ofKind("failing")),
                  },
                  {
                    value: "slower",
                    label: countLabel(t("alerts.kind.slower"), ofKind("slower")),
                  },
                  {
                    value: "stopped",
                    label: countLabel(t("alerts.kind.stopped"), ofKind("stopped")),
                  },
                ]}
              />
              <DensityToggle value={density} onChange={setDensity} />
            </>
          }
          rowActions={{
            label: t("actions.column"),
            width: 56,
            render: (alert) => (
              <ViewRunsButton app={alert} onClick={() => onViewRuns(alert.appId)} />
            ),
          }}
          pagination={{ kind: "offset", total: page.total, labels: paginationLabels() }}
          messages={tableMessages({ empty: { title: t("alerts.empty") } })}
        />
        <p className="text-xs text-faint">
          <Trans t={t} i18nKey="alerts.rules" components={RULE_TERM} />
        </p>
      </div>
    </div>
  );
}
